'use client'
import {RefObject, useCallback, useEffect, useRef} from 'react'
import {useInView} from 'react-intersection-observer'
import {cx} from '@local/utils/cx'
import {lerp} from '@local/utils/maths'
import useRaf from '@local/hooks/useRaf'
import {useResizeObserver} from '@local/hooks'

interface LottieProps {
	filePath: string
	progress?: RefObject<number | null>
	renderer?: 'canvas'
	loop?: boolean
	autoplay?: boolean
	className?: string
}

export const Lottie = ({filePath, progress = null, renderer = 'canvas', loop = true, autoplay = true, className}: LottieProps) => {
	const ref = useRef<HTMLDivElement>(null)
	const lottieRef = useRef<any>(null)
	const loaded = useRef<boolean>(false)
	const [inViewRef, inView] = useInView({
		rootMargin: '20%',
	})

	const setRefs = useCallback(node => {
		ref.current = node
		inViewRef(node)
	}, [inViewRef])

	useEffect(() => {
		if (!loaded.current && inView) {
			loaded.current = true

			import('lottie-web').then((lottie) => {
				// @ts-expect-error `lottie` is of type `LottiePlayer` that got `.loadAnimation()`
				lottieRef.current = lottie.loadAnimation({
					container: ref.current,
					renderer,
					loop,
					autoplay: progress ? false : autoplay, // disable autoplay on scroll driven
					path: filePath,
					rendererSettings: {
						preserveAspectRatio: 'xMidYMid meet',
						// clearCanvas: false,
					},
				})

				lottieRef.current.addEventListener('data_ready', () => {
					ref.current.classList.add('opacity-100')
				})
			})
		} else if (lottieRef.current) {
			if (inView) {
				lottieRef.current.play()
			} else {
				lottieRef.current.pause()
			}
		}
	}, [inView, renderer, filePath, loop, autoplay])

	// scroll driven
	useRaf(() => {
		if (progress && lottieRef?.current) {
			const playhead = lerp(0, lottieRef.current.totalFrames, progress.current)
			lottieRef.current.goToAndStop(playhead, true)
		}
	})

	useResizeObserver(ref, () => {
		if (lottieRef.current) {
			lottieRef.current.resize()
		}
	})

	useEffect(() => {
		return () => {
			if (lottieRef.current) lottieRef.current.destroy()
		}
	}, [])

	return (
		<div
			ref={setRefs}
			className={cx(
				'opacity-0 transition-opacity duration-500 ease-quart-out',
				className,
			)} />
	)
}

// .lottie {
// 	opacity: 0;
// 	transition: all 0.5s $quart-out;
// 	transition-property: opacity;
// }

// .isLoaded {
// 	opacity: 1;
// }

export default Lottie
